import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Spinner, Alert } from 'react-bootstrap';
import api from '../../config/ApiConfig';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
    }
  },
  hidePostalCode: true
};

const StripePaymentForm = ({ orderData, totalTTC }) => {
  const stripe = useStripe();
  const elements = useElements();
  const history = useHistory();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // 1. Créer le PaymentIntent côté backend
      const { data } = await api.post('/fo/create-payment-intent', {
        amount: Math.round(totalTTC * 100), // Stripe utilise les centimes
        orderData: orderData
      });

      const { clientSecret, orderId, paymentIntentId } = data;

      // 2. Confirmer le paiement avec Stripe
      const cardElement = elements.getElement(CardElement);

      // Extract postal code from address (French format)
      const extractPostalCode = (address) => {
        // Match French postal codes (5 digits)
        const match = address?.match(/\b\d{5}\b/);
        return match ? match[0] : '06560'; // Default to Valbonne if not found
      };

      const postalCode = extractPostalCode(orderData.adresseLivraison);

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: `${orderData.prenomClient} ${orderData.nomClient}`,
              email: orderData.email,
              phone: orderData.telephone,
              address: {
                line1: orderData.adresseLivraison,
                postal_code: postalCode,
                country: 'FR'
              }
            }
          }
        }
      );

      if (stripeError) {
          setError(stripeError.message);
          setProcessing(false);
          toast.error(`Erreur de paiement: ${stripeError.message}`);

          // 🔥 IMPORTANT : informer le backend du paiement échoué
          try {
              await api.post('/fo/confirm-order-payment', {
                  orderId: orderId,
                  paymentIntentId: paymentIntentId // récupéré du backend lors du create-payment-intent
              });
          } catch (apiError) {
              console.warn("Erreur lors de la confirmation du paiement KO :", apiError);
          }

          // Redirection vers l’échec
          history.push({
              pathname: '/payment-failed',
              state: {
                  error: stripeError.message,
                  orderId: orderId
              }
          });
          return;
      }


      // 3. Paiement réussi
      if (paymentIntent.status === 'succeeded') {
        // Confirmer la commande côté backend
        await api.post('/fo/confirm-order-payment', {
          orderId: orderId,
          paymentIntentId: paymentIntent.id
        });

        toast.success('Paiement réussi !');

        // Rediriger vers la page de confirmation
        history.push({
          pathname: '/payment-success',
          state: {
            orderId: orderId,
            paymentIntentId: paymentIntent.id,
            amount: totalTTC
          }
        });
      }
    } catch (err) {
      console.error('Erreur lors du paiement:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Une erreur est survenue';
      setError(errorMessage);
      toast.error(errorMessage);
      setProcessing(false);

      // Rediriger vers la page d'échec
      history.push({
        pathname: '/payment-failed',
        state: { error: errorMessage }
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-payment-form">
      <div className="mb-4">
        <h5 className="mb-3">Informations de paiement</h5>
        <div className="p-3 border rounded bg-white">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">
          <strong>Erreur:</strong> {error}
        </Alert>
      )}

      <div className="d-grid gap-2">
        <button
          type="submit"
          className="btn btn-success btn-lg"
          disabled={!stripe || processing}
        >
          {processing ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Paiement en cours...
            </>
          ) : (
            `Payer ${totalTTC.toFixed(2)} €`
          )}
        </button>
      </div>

      <div className="text-center mt-3 text-muted small">
        <p>Paiement sécurisé par Stripe</p>
      </div>
    </form>
  );
};

export default StripePaymentForm;
